import type { HttpClient, HttpEvent, HttpRequestOptions, HttpResponse } from "./types.js";
import type { RateLimiter } from "./rate-limiter.js";
import type { RetryOptions } from "../data-model/options.js";
import type { Url } from "../data-model/primitives.js";
import { CancelledError, HttpError, ParseError, RateLimitedError } from "../core/errors.js";
import type { Logger } from "../utils/logger.js";
import { hostnameOf } from "../utils/url.js";
import { sleep } from "../utils/sleep.js";

export const DEFAULT_RETRY = {
  retries: 3,
  backoff: "exponential",
  minDelayMs: 500,
  maxDelayMs: 30_000,
  retryOn: [408, 429, 500, 502, 503, 504, 524],
} as const satisfies Required<RetryOptions>;

export class RetryingHttpClient implements HttpClient {
  constructor(
    private readonly inner: HttpClient,
    private readonly opts: Required<RetryOptions>,
    private readonly logger: Logger,
    private readonly rateLimiter?: RateLimiter,
    private readonly onEvent?: (e: HttpEvent) => void,
  ) {}

  async get(url: Url, reqOpts?: HttpRequestOptions): Promise<HttpResponse> {
    let attempt = 0;
    while (true) {
      try {
        const res = await this.inner.get(url, reqOpts);
        if (this.opts.retryOn.includes(res.status)) {
          if (attempt >= this.opts.retries) {
            if (res.status === 429) {
              throw new RateLimitedError(url, parseRetryAfter(res));
            }
            throw new HttpError(res.status, url);
          }
          const wait = this.#computeBackoff(res, attempt);
          if (res.status === 429 && this.rateLimiter) {
            this.rateLimiter.pauseHost(hostnameOf(url), wait);
          }
          this.logger.debug({ url, status: res.status, attempt, wait }, "http retry");
          this.onEvent?.({
            type: "http:retry",
            url,
            attempt,
            waitMs: wait,
            status: res.status,
          });
          await sleep(wait, reqOpts?.signal);
          attempt++;
          continue;
        }
        return res;
      } catch (err) {
        if (
          err instanceof CancelledError ||
          err instanceof ParseError ||
          err instanceof RateLimitedError ||
          (err instanceof HttpError && err.status > 0)
        ) {
          throw err;
        }
        if (attempt >= this.opts.retries) throw err;
        const wait = this.#computeBackoff(null, attempt);
        this.logger.debug(
          { url, errMessage: err instanceof Error ? err.message : String(err), attempt, wait },
          "http retry (error)",
        );
        this.onEvent?.({ type: "http:retry", url, attempt, waitMs: wait });
        await sleep(wait, reqOpts?.signal);
        attempt++;
      }
    }
  }

  #computeBackoff(res: HttpResponse | null, attempt: number): number {
    if (res) {
      const ra = parseRetryAfter(res);
      if (ra != null) return clamp(ra, this.opts.minDelayMs, this.opts.maxDelayMs);
    }
    const base = this.opts.minDelayMs * Math.pow(2, attempt);
    const jitter = Math.floor(Math.random() * (base * 0.5));
    return clamp(base + jitter, this.opts.minDelayMs, this.opts.maxDelayMs);
  }
}

function parseRetryAfter(res: HttpResponse): number | undefined {
  const raw = res.headers["retry-after"];
  if (!raw) return undefined;
  const asInt = Number.parseInt(raw, 10);
  if (Number.isFinite(asInt) && String(asInt) === raw.trim()) {
    return asInt * 1000;
  }
  const asDate = Date.parse(raw);
  if (Number.isFinite(asDate)) {
    return Math.max(0, asDate - Date.now());
  }
  return undefined;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}
